import { Heading, HeadingLevel } from "@ariakit/react";
import {
  Success,
  swrResponseToResult,
  type RedeemedPledge,
} from "@indietabletop/appkit";
import { useCallback, type ReactNode } from "react";
import useSWR from "swr";
import { Link, useParams } from "wouter";
import { client } from "../../client.ts";
import {
  DownloadButton,
  DownloadSection,
} from "../DownloadSection/DownloadSection.tsx";
import { FailureView } from "../failures/FailureView.tsx";
import { LoadingView } from "../LoadingView/LoadingView.tsx";
import { Page } from "../Page/Page.tsx";
import { appSection } from "./style.css.ts";

export function Center(props: { children?: ReactNode }) {
  return (
    <div
      style={{
        minBlockSize: "100svh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {props.children}
    </div>
  );
}

function TryAppSection() {
  return (
    <div className={appSection.container}>
      <div className={appSection.header}>
        <p className={appSection.overline}>Oi! Hey there!</p>
        <Heading className={appSection.heading}>Fancy a gang builder?</Heading>
      </div>

      <p className={appSection.body}>
        Create a gang in <em>literal seconds</em>. Includes print view, sharing,
        build modes, campaign tracking, and more!
      </p>

      <Link href="~/" className={appSection.action}>
        Explore the app
      </Link>
    </div>
  );
}

export function FulfilmentPageSuccess(props: { pledge: RedeemedPledge }) {
  const { pledge } = props;

  return (
    <div>
      <DownloadSection
        action={
          <DownloadButton href={pledge.downloadUrl}>Download</DownloadButton>
        }
      />
      <HeadingLevel>
        <TryAppSection />
      </HeadingLevel>
    </div>
  );
}

export function RedeemPage() {
  const { id } = useParams<{ id: string }>();

  const result = swrResponseToResult(
    useSWR(
      `redeem:${id}`,
      useCallback(async () => {
        // TODO (Phase 4): Move this to the API layer or the client
        if (id === "test") {
          await new Promise((r) => setTimeout(r, 1000));

          type FakeSuccess = Awaited<ReturnType<typeof client.redeemPledge>>;
          const fakeSuccess: FakeSuccess = new Success({
            id: "test",
            downloadUrl: "/test.pdf",
            downloadUrlExpiresTs: 0,
            contactSubscribed: false,
            email: "test@example.com",
          });

          return fakeSuccess;
        }

        return client.redeemPledge("spacegits", id);
      }, [id]),
      {
        focusThrottleInterval: 60 * 60 * 1000,
      },
    ),
  );

  if (result.isPending) {
    return (
      <Page backHref="~/" title="Rewards" centerChildren>
        <LoadingView />
      </Page>
    );
  }

  if (result.isFailure) {
    return (
      <Page backHref="~/" title="Rewards" centerChildren>
        <FailureView
          failure={result.failure}
          messages={{
            404: {
              title: "Pledge not found",
            },
            403: {
              title: "Link deactivated",
              description: `This link was deactivated by campaign owner. Get in touch with them for more information.`,
            },
          }}
        />
      </Page>
    );
  }

  return (
    <Page title="Rewards" backHref="~/">
      <FulfilmentPageSuccess pledge={result.value} />
    </Page>
  );
}
